import { ApiError } from './ApiError';
import type { ApiErrorBody } from '@/types/api';

/**
 * The only place in the application that calls `fetch`.
 *
 * <p>Centralising it means the auth header, the error shape, the idempotency key and the
 * timeout are applied uniformly. A component that calls `fetch` directly gets none of
 * them, and the omission is invisible until it matters.
 */

/** Blank base URL means same-origin, which is how CloudFront serves this in AWS. */
const BASE_URL = (import.meta.env['VITE_API_BASE_URL'] ?? '').replace(/\/$/, '');

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Supplies the bearer token.
 *
 * <p>Injected rather than imported so the client does not depend on the auth feature --
 * that would be a cycle, and it would make every API test need a Cognito mock. The auth
 * provider registers a getter at startup.
 */
type TokenProvider = () => string | null | Promise<string | null>;

let tokenProvider: TokenProvider = () => null;
let extraHeaders: () => Record<string, string> = () => ({});
let onAuthFailure: () => void = () => {};

export function configureApiClient(options: {
  getAccessToken: TokenProvider;
  onAuthFailure?: () => void;
  /** Extra request headers. Used only by the local-development identity. */
  getExtraHeaders?: () => Record<string, string>;
}): void {
  tokenProvider = options.getAccessToken;
  if (options.getExtraHeaders) {
    extraHeaders = options.getExtraHeaders;
  }
  if (options.onAuthFailure) {
    onAuthFailure = options.onAuthFailure;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Required by the API on seat- and money-affecting writes. Generate it once per user
   * intent and reuse it across retries of that same intent -- a fresh key per HTTP
   * attempt would defeat the entire mechanism and could double-book a passenger.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, idempotencyKey, signal, timeoutMs } = options;

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const headers: Record<string, string> = { Accept: 'application/json', ...extraHeaders() };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (idempotencyKey) {
    headers['Idempotency-Key'] = idempotencyKey;
  }

  const token = await tokenProvider();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // A hung request is worse than a failed one: it blocks the UI with no way out. The
  // caller's own signal is honoured alongside the timeout.
  const timeout = new AbortController();
  const timer = window.setTimeout(() => timeout.abort(), timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const composed = signal ? anySignal([signal, timeout.signal]) : timeout.signal;

  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? null : JSON.stringify(body),
      signal: composed,
      credentials: 'same-origin',
    });
  } catch (cause) {
    window.clearTimeout(timer);
    // An aborted request the caller asked for should propagate as an abort, so
    // TanStack Query can tell cancellation apart from failure.
    if (signal?.aborted) {
      throw cause;
    }
    throw ApiError.fromUnknown(0);
  } finally {
    window.clearTimeout(timer);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const payload = text ? safeParse(text) : null;

  if (!response.ok) {
    const error = isApiErrorBody(payload)
      ? new ApiError(response.status, payload)
      : ApiError.fromUnknown(response.status);

    // A 401 anywhere means the session is gone. Handling it once here beats every
    // caller remembering to, and forgetting leaves the user stuck on a broken page.
    if (error.isAuthFailure) {
      onAuthFailure();
    }
    throw error;
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    // An HTML error page from a proxy, or a truncated body. Not JSON, not our problem
    // to interpret -- the caller gets a generic ApiError rather than a parse crash.
    return null;
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiErrorBody).code === 'string' &&
    typeof (value as ApiErrorBody).message === 'string'
  );
}

/** `AbortSignal.any` is not available on every browser we support yet. */
function anySignal(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}
